import React, { useRef, useState } from 'react';
import { X, Image, Type, Download, Share2, Check, Loader2, Copy } from 'lucide-react';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';
import { API_URL } from '../services/apiConfig';

import { isNativeAlarmAvailable } from '../services/nativeAlarmBridge';

interface ShareContent {
  title: string;
  text: string;
  imageRef?: React.RefObject<HTMLDivElement>;
  url?: string;
  fileName?: string;
}

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  content: ShareContent;
}

const APP_URL = API_URL || window.location.origin;

export const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, content }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const shareText = `${content.text}\n\n${content.url || APP_URL}`;

  const handleTextShare = async () => {
    // Native Android: use NativeAlarm bridge
    if (isNativeAlarmAvailable() && (window as any).NativeAlarm?.shareText) {
      const result = JSON.parse((window as any).NativeAlarm.shareText(content.title || '', shareText));
      if (result.success) {
        toast.success('शेयर हो रहा है!');
        onClose();
        return;
      }
    }

    // For long text (>2000 chars), copy to clipboard first then open share
    // WhatsApp/navigator.share truncates long text
    const isLongText = shareText.length > 2000;

    if (isLongText) {
      // Copy full text to clipboard first
      try {
        await navigator.clipboard.writeText(shareText);
      } catch {}

      // Try share with truncated text + instruction
      const truncatedText = `${content.title}\n\n${shareText.slice(0, 1500)}...\n\n[पूरा पढ़ने के लिए ऐप खोलें]\n${content.url || APP_URL}`;
      
      if (navigator.share) {
        try {
          await navigator.share({ title: content.title, text: truncatedText });
          toast.success('शेयर हो गया! पूरा टेक्स्ट क्लिपबोर्ड में भी कॉपी है');
          onClose();
          return;
        } catch (e: any) {
          if (e.name === 'AbortError') return;
        }
      }
      toast.success('पूरा टेक्स्ट कॉपी हो गया! WhatsApp में पेस्ट करें', { duration: 5000 });
      onClose();
      return;
    }

    // Short text - share normally
    if (navigator.share) {
      try {
        await navigator.share({ title: content.title, text: shareText });
        toast.success('शेयर हो गया!');
        onClose();
      } catch (e: any) {
        if (e.name !== 'AbortError') {
          await copyToClipboard(shareText);
        }
      }
    } else {
      await copyToClipboard(shareText);
    }
  };

  const handleImageShare = async () => {
    if (!content.imageRef?.current) {
      toast.error('चित्र बनाने में समस्या हुई');
      return;
    }

    setIsGenerating(true);
    try {
      const canvas = await html2canvas(content.imageRef.current, {
        backgroundColor: '#FDF5E6',
        scale: 3,
        useCORS: true,
        allowTaint: true,
        logging: false,
      });

      const fileName = content.fileName || `golok-share-${Date.now()}.png`;

      // Native Android: use NativeAlarm bridge for reliable sharing
      if (isNativeAlarmAvailable() && (window as any).NativeAlarm?.shareImage) {
        const base64 = canvas.toDataURL('image/png');
        const shareTextStr = `${content.title}\n\n${content.url || APP_URL}`;
        const result = JSON.parse((window as any).NativeAlarm.shareImage(base64, fileName, shareTextStr));
        if (result.success) {
          toast.success('शेयर हो रहा है!');
          onClose();
          return;
        }
      }

      // Fallback: Web Share API
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/png')
      );

      if (!blob) {
        throw new Error('Blob creation failed');
      }

      if (navigator.share && navigator.canShare) {
        const file = new File([blob], fileName, { type: 'image/png' });
        const shareData = { files: [file], title: content.title, text: content.text };

        if (navigator.canShare(shareData)) {
          try {
            await navigator.share(shareData);
            toast.success('शेयर हो गया!');
            onClose();
            return;
          } catch (e: any) {
            if (e.name === 'AbortError') return;
          }
        }
      }

      // Fallback: download image
      const link = document.createElement('a');
      link.download = fileName;
      link.href = URL.createObjectURL(blob!);
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success('चित्र डाउनलोड हो गया!');
    } catch (error) {
      console.error('Image share error:', error);
      toast.error('चित्र बनाने में समस्या हुई');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('कॉपी हो गया! अब WhatsApp/Telegram में पेस्ट करें');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('कॉपी नहीं हो पाया');
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center animate-fade-in" data-testid="share-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-sm mx-4 mb-4 sm:mb-0 bg-white dark:bg-stone-800 rounded-2xl shadow-2xl overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-stone-700">
          <div className="flex items-center gap-2">
            <Share2 size={18} className="text-saffron" />
            <h3 className="font-bold text-gray-800 dark:text-stone-200">शेयर करें</h3>
          </div>
          <button
            onClick={onClose}
            data-testid="share-modal-close"
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-stone-700 rounded-full transition-colors"
          >
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Options */}
        <div className="p-4 space-y-3">
          {/* Image Share */}
          {content.imageRef && (
            <button
              onClick={handleImageShare}
              disabled={isGenerating}
              data-testid="share-as-image-btn"
              className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-100 dark:border-stone-700 hover:border-saffron/40 hover:bg-saffron/5 dark:hover:bg-saffron/10 transition-all group"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center shrink-0 shadow-md">
                {isGenerating ? (
                  <Loader2 size={20} className="text-white animate-spin" />
                ) : (
                  <Image size={20} className="text-white" />
                )}
              </div>
              <div className="text-left flex-1">
                <p className="font-bold text-gray-800 dark:text-stone-200 text-sm">
                  {isGenerating ? 'चित्र बना रहे हैं...' : 'चित्र के रूप में शेयर करें'}
                </p>
                <p className="text-xs text-gray-400 dark:text-stone-500">
                  सुंदर poster बनेगा — WhatsApp, Instagram पर भेजें
                </p>
              </div>
            </button>
          )}

          {/* Text Share */}
          <button
            onClick={handleTextShare}
            data-testid="share-as-text-btn"
            className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-100 dark:border-stone-700 hover:border-saffron/40 hover:bg-saffron/5 dark:hover:bg-saffron/10 transition-all group"
          >
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shrink-0 shadow-md">
              <Type size={20} className="text-white" />
            </div>
            <div className="text-left flex-1">
              <p className="font-bold text-gray-800 dark:text-stone-200 text-sm">
                टेक्स्ट शेयर करें
              </p>
              <p className="text-xs text-gray-400 dark:text-stone-500">
                {shareText.length > 2000 ? 'सारांश शेयर होगा, पूरा टेक्स्ट कॉपी भी होगा' : 'WhatsApp, Telegram या कहीं भी भेजें'}
              </p>
            </div>
          </button>

          {/* Copy FULL Text */}
          <button
            onClick={() => copyToClipboard(shareText)}
            data-testid="share-copy-btn"
            className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-100 dark:border-stone-700 hover:border-saffron/40 hover:bg-saffron/5 dark:hover:bg-saffron/10 transition-all group"
          >
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shrink-0 shadow-md">
              {copied ? <Check size={20} className="text-white" /> : <Copy size={20} className="text-white" />}
            </div>
            <div className="text-left flex-1">
              <p className="font-bold text-gray-800 dark:text-stone-200 text-sm">
                {copied ? 'कॉपी हो गया!' : 'पूरा टेक्स्ट कॉपी करें'}
              </p>
              <p className="text-xs text-gray-400 dark:text-stone-500">
                {shareText.length > 2000 ? `पूरा ${Math.round(shareText.length/100)*100}+ अक्षर कॉपी होंगे` : 'क्लिपबोर्ड में कॉपी करें'}
              </p>
            </div>
          </button>
        </div>

        {/* Preview */}
        <div className="px-4 pb-4">
          <div className="bg-gray-50 dark:bg-stone-900 rounded-lg p-3 text-xs text-gray-500 dark:text-stone-500 line-clamp-3">
            {content.text.slice(0, 120)}...
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Simple share button that can be dropped into any component.
 * Pass content and optionally an imageRef for image sharing.
 */
export const ShareButton: React.FC<{
  content: ShareContent;
  className?: string;
  iconSize?: number;
  label?: string;
}> = ({ content, className, iconSize = 16, label }) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowModal(true);
        }}
        data-testid="share-button"
        className={className || "flex items-center gap-1.5 text-gray-500 dark:text-stone-400 hover:text-saffron transition-colors"}
      >
        <Share2 size={iconSize} />
        {label && <span className="text-xs font-medium">{label}</span>}
      </button>
      <ShareModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        content={content}
      />
    </>
  );
};
